<template>
  <form
    @click="activateSearch"
    @submit.prevent
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    class="relative flex items-center h-8 transition-all duration-1000 rounded-md cursor-pointer">
    <input
      v-model="searchQuery"
      @keydown.enter="onSearch"
      @focusout="onSearch"
      @input="inputChanged = true"
      @focus="focused = true"
      autocomplete="off"
      ref="searchInput"
      type="text"
      :disabled="mode == 'view'"
      :placeholder="placeholderText ? placeholderText : $t('common.actions.search')"
      :class="inputClass" />
    <span
      v-if="mode === 'edit' && searchQuery && searchQuery !== ''"
      @click.stop="clearSearch"
      class="absolute text-gray-400 cursor-pointer right-2">
      <IconXmark class="size-4" />
    </span>
    <span
      v-if="mode === 'edit' && (!searchQuery || searchQuery === '' || fullMode)"
      class="absolute text-gray-400 cursor-none"
      :class="fullMode ? 'left-2' : 'right-2'">
      <IconSearch class="size-4" />
    </span>
    <Teleport to="body">
      <div
        v-if="tooltipVisible && error"
        ref="tooltipRef"
        :style="{
          position: strategy,
          top: y ? `${y}px` : '',
          left: x ? `${x}px` : '',
          zIndex: 9999,
        }"
        class="relative flex flex-col items-center px-3 py-2 text-white bg-red-500 rounded-md shadow-lg">
        <span class="text-xs truncate">{{ error }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="absolute w-4 h-4 text-red-500 fill-current"
          :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
          viewBox="0 0 24 24">
          <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
        </svg>
      </div>
    </Teleport>
  </form>
</template>

<script setup lang="ts">
import { ref, nextTick, watchEffect, computed, watch } from 'vue';
import IconSearch from '@/assets/icons/IconSearch.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import { useFloating, autoUpdate, offset, flip, shift, hide } from '@floating-ui/vue';
import { cva } from 'class-variance-authority';

const searchInput = ref<HTMLElement | null>(null);
const searchQuery = defineModel<string>();
const inputChanged = ref(false);

const emit = defineEmits(['updateSearch', 'clearSelection']);

const props = defineProps<{
  error: string;
  fullMode?: boolean;
  placeholderText?: string;
  mode: 'edit' | 'view';
  lineFocusInput?: boolean;
}>();

const isActive = ref(false);
const activateSearch = () => {
  isActive.value = true;
  nextTick(() => {
    searchInput.value?.focus();
  });
};

const clearSearch = () => {
  if (!searchQuery.value) return;
  searchQuery.value = '';
  emit('clearSelection');
};

function onSearch() {
  if (inputChanged.value) emit('updateSearch');
  inputChanged.value = false;
  focused.value = false;
}

const tooltipRef = ref<HTMLElement | null>(null);
const tooltipVisible = ref(false);

const { x, y, strategy, update, middlewareData } = useFloating(searchInput, tooltipRef, {
  placement: 'top',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, update) => {
    return autoUpdate(referenceEl, floatingEl, update);
  },
});

function showTooltip() {
  tooltipVisible.value = true;
  update();
}

function hideTooltip() {
  tooltipVisible.value = false;
}

watchEffect(() => {
  if (middlewareData.value.hide?.referenceHidden) {
    tooltipVisible.value = false;
  }
});

const focused = ref(false);

const inputClass = computed(() => {
  return cva('w-full h-full px-2 text-sm font-light border-none  cursor-pointer  ', {
    variants: {
      mode: {
        edit: 'text-gray-900 bg-white',
        view: 'bg-gray-50 pointer-events-none',
      },
      error: {
        true: '',
        false: 'text-gray-600 outline outline-1 outline-gray-300',
      },
      fullMode: {
        true: 'bg-gray-100 pl-8',
        false: 'rounded-md',
      },
      focused: {
        true: '',
      },
    },
    compoundVariants: [
      {
        focused: true,
        error: true,
        class: 'outline outline-theme-500 outline-2 shadow-sm',
      },
      { focused: false, error: true, class: 'text-red-500 outline outline-red-500 outline-1' },
    ],
  })({
    mode: props.mode,
    error: !!props.error,
    fullMode: !!props.fullMode,
    focused: focused.value,
  });
});

watch(
  () => props.lineFocusInput,
  (val) => {
    if (!val) return;
    nextTick(() => {
      searchInput.value?.focus();
    });
  },
  {
    flush: 'post',
    immediate: true,
  }
);
</script>
